import { useEffect, useMemo, useState } from "react"
import type { AppShell } from "../App"
import { loadGoals, type GoalKr } from "../lib/api"
import { cn } from "../lib/utils"

/**
 * Objetivos do trimestre.
 *
 * A barra é a **medição** (snapshot, automática); o chip é a **confiança** que
 * o dono declarou no check-in. Vivem separados desde o plano de RH — o
 * check-in nunca sobrepõe o valor de um KR medido por query. O que faltava era
 * a vista que os cruza: um 🟢 com a medição muito abaixo do ritmo do trimestre
 * é a conversa mais útil que um OKR consegue gerar.
 */

const CONFIDENCE = {
  on_track: { label: "No caminho", cls: "border-emerald-500 text-emerald-600 dark:text-emerald-400" },
  at_risk: { label: "Em risco", cls: "border-amber-500 text-amber-600 dark:text-amber-400" },
  off_track: { label: "Fora do caminho", cls: "border-destructive text-destructive" },
} as const

/** Check-in mais velho do que isto deixa de ser informação. */
const STALE_CHECKIN_DAYS = 7

/**
 * Ritmo do trimestre **dos objetivos**, não o de hoje.
 *
 * Semear o Q4 em Agosto e mostrar o ritmo do Q3 daria uma barra a 60% contra
 * KRs que ainda não começaram — e um board vazio sem dizer porquê.
 */
function quarterPace(period: string | null): {
  pct: number
  label: string
  day: number
  total: number
  started: boolean
} {
  const now = new Date()
  const match = period ? /^(\d{4})-Q([1-4])$/.exec(period) : null
  const year = match ? Number(match[1]) : now.getFullYear()
  const q = match ? Number(match[2]) - 1 : Math.floor(now.getMonth() / 3)
  const start = new Date(year, q * 3, 1)
  const end = new Date(year, q * 3 + 3, 1)
  const total = Math.round((end.getTime() - start.getTime()) / 86_400_000)
  const day = Math.round((now.getTime() - start.getTime()) / 86_400_000)
  return {
    pct: Math.max(0, Math.min(1, day / total)),
    label: `${year}-Q${q + 1}`,
    day: Math.max(0, Math.min(day, total)),
    total,
    started: now >= start,
  }
}

function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
}

export function OkrPage({ shell }: { shell: AppShell }) {
  const [goals, setGoals] = useState<GoalKr[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Podem coexistir vários trimestres semeados (piloto + o seguinte). Mostra-se
  // um só: o corrente se existir, senão o mais próximo. Misturar dois períodos
  // punha a barra de ritmo de um contra os KRs do outro.
  const period = useMemo(() => {
    const periods = Array.from(new Set((goals ?? []).map((g) => g.period).filter(Boolean))) as string[]
    if (periods.length === 0) return null
    const now = new Date()
    const current = `${now.getFullYear()}-Q${Math.floor(now.getMonth() / 3) + 1}`
    return periods.includes(current) ? current : periods.sort()[0]
  }, [goals])

  const visible = useMemo(
    () => (goals ?? []).filter((g) => !period || g.period === period),
    [goals, period],
  )
  const pace = useMemo(() => quarterPace(period), [period])

  useEffect(() => {
    loadGoals(shell.client)
      .then((rows) => {
        setGoals(rows)
        setError(null)
      })
      .catch((err) => setError((err as Error).message))
  }, [shell.client])

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-lg font-semibold text-destructive">Não foi possível ler os objetivos</h1>
        <p className="mt-1 text-sm text-muted-foreground">{error}</p>
      </div>
    )
  }
  if (!goals) return <div className="p-6 text-sm text-muted-foreground">A carregar objetivos…</div>

  const measured = visible.filter((g) => g.data_source && g.data_source !== "manual").length
  const diverging = visible.filter((g) => isDiverging(g, pace.pct)).length

  const byParent = new Map<string, { title: string; krs: GoalKr[] }>()
  for (const kr of visible) {
    const key = kr.parent_id ?? "sem-objetivo"
    const entry = byParent.get(key) ?? {
      title: kr.objective_title ?? "Sem objetivo associado",
      krs: [],
    }
    entry.krs.push(kr)
    byParent.set(key, entry)
  }

  return (
    <div className="mx-auto max-w-[1200px] px-6 pb-16">
      <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-md border bg-border sm:grid-cols-5">
        <Kpi
          value={pace.label}
          label="Trimestre"
          sub={pace.started ? `dia ${pace.day} de ${pace.total}` : "ainda não começou"}
        />
        <Kpi
          value={`${Math.round(pace.pct * 100)} %`}
          label="decorrido"
          sub={pace.started ? "o ritmo a bater" : "arranca no início do trimestre"}
        />
        <Kpi value={String(byParent.size)} label="objetivos" sub={`${visible.length} key results`} />
        <Kpi value={String(measured)} label="KR medidos sozinhos" sub={`${goals.length - measured} manuais`} />
        <Kpi value={String(diverging)} label="divergências" sub="confiança ≠ medição" />
      </div>

      <div className="mb-3 mt-9 flex flex-wrap items-baseline gap-3 border-b pb-2">
        <h2 className="text-base font-semibold">Objetivos do trimestre</h2>
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          a marca escura é o ritmo esperado
        </span>
        <span className="ml-auto text-xs text-muted-foreground">
          A percentagem é medida. A cor é o que o dono diz.
        </span>
      </div>

      {visible.length === 0 ? (
        <p className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
          Sem key results ativos. Semeia os objetivos do trimestre ou verifica se
          estão marcados como visíveis para a equipa.
        </p>
      ) : (
        [...byParent.entries()].map(([key, group]) => (
          <div key={key} className="mb-4 overflow-hidden rounded-md border bg-card">
            <div className="flex flex-wrap items-baseline gap-3 border-b bg-muted/50 px-4 py-3">
              <h3 className="text-base font-semibold">{group.title}</h3>
            </div>
            {group.krs.map((kr) => (
              <KrRow key={kr.id} kr={kr} pace={pace.pct} />
            ))}
          </div>
        ))
      )}

      <p className="mt-6 border-l-2 border-primary bg-primary/5 px-4 py-3 text-sm">
        <b>O ciclo:</b> o trimestre define para onde vamos · o Radar decide onde focar esta semana ·
        o Que Resolvemos prova o que andou. Um KR sem check-in há mais de {STALE_CHECKIN_DAYS} dias
        entra na revisão de segunda, e não numa cerimónia à parte.
      </p>
    </div>
  )
}

function isDiverging(kr: GoalKr, pace: number): boolean {
  const progress = (kr.progress_pct ?? 0) / 100
  return kr.latest_checkin?.confidence === "on_track" && progress < pace - 0.12
}

function KrRow({ kr, pace }: { kr: GoalKr; pace: number }) {
  const progress = Math.max(0, Math.min(1, (kr.progress_pct ?? 0) / 100))
  const behind = progress < pace - 0.12
  const confidence = kr.latest_checkin?.confidence as keyof typeof CONFIDENCE | undefined
  const meta = confidence ? CONFIDENCE[confidence] : null
  const age = daysSince(kr.latest_checkin?.checked_at)
  const stale = age !== null && age > STALE_CHECKIN_DAYS
  const auto = Boolean(kr.data_source && kr.data_source !== "manual")

  return (
    <div className="border-b px-4 py-3 last:border-0">
      <div className="mb-2 flex flex-wrap items-baseline gap-2">
        <span className="text-sm font-medium">{kr.title}</span>
        <span
          className={cn(
            "rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider",
            auto ? "border-primary text-primary" : "border-dashed border-amber-500 text-amber-600",
          )}
        >
          {auto ? "medido sozinho" : "manual"}
        </span>
      </div>

      <div className="mb-2 flex items-center gap-3">
        <span className="relative h-2 flex-1 overflow-hidden rounded-full bg-muted">
          <i
            className={cn(
              "block h-full rounded-full",
              behind ? (progress < pace - 0.3 ? "bg-destructive" : "bg-amber-500") : "bg-primary",
            )}
            style={{ width: `${Math.max(progress * 100, 2)}%` }}
          />
          <span
            className="absolute -top-1 bottom-[-4px] w-0.5 bg-foreground/50"
            style={{ left: `${pace * 100}%` }}
          />
        </span>
        <span className="whitespace-nowrap font-mono text-xs text-muted-foreground">
          <b className="text-foreground">{kr.current_value ?? 0}</b> / {kr.target_value ?? "—"} ·{" "}
          {Math.round(progress * 100)}%
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-sm">
        {meta ? (
          <span
            className={cn(
              "rounded border px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider",
              meta.cls,
            )}
          >
            {meta.label}
          </span>
        ) : (
          <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            sem check-in
          </span>
        )}
        {age !== null && (
          <span className={cn("text-xs", stale ? "text-amber-600" : "text-muted-foreground")}>
            check-in há {age} dia{age === 1 ? "" : "s"}
            {stale && " — está velho"}
          </span>
        )}
      </div>

      {isDiverging(kr, pace) && (
        <p className="mt-2 border-l-2 border-destructive bg-destructive/10 px-3 py-2 text-sm">
          <b className="text-destructive">
            Confiança diz “no caminho”, a medição diz {Math.round(progress * 100)}%
          </b>{" "}
          com {Math.round(pace * 100)}% do trimestre gasto. É a conversa a ter na segunda.
        </p>
      )}
    </div>
  )
}

function Kpi({ value, label, sub }: { value: string; label: string; sub: string }) {
  return (
    <div className="bg-card px-4 py-3">
      <div className="font-mono text-xl font-semibold leading-none">{value}</div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="text-[11px] text-muted-foreground">{sub}</div>
    </div>
  )
}
